<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch, type PropType } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import type { MenuSection } from '@botto/core';
import { jwtHasPermission, type ApiAudience } from '@botto/core';
import bottoLogo from '../assets/botto-logo.png';

const props = defineProps({
  sections: { type: Array as PropType<MenuSection[]>, default: () => [] },
});

const route = useRoute();
const scrollEl = ref<HTMLElement | null>(null);
const fadeTop = ref(false);
const fadeBottom = ref(false);

let resizeObserver: ResizeObserver | null = null;

function isActive(to: string | { name: string }): boolean {
  if (typeof to === 'string') return route.path === to || route.path.startsWith(to + '/');
  return route.name === to.name;
}

function isSectionActive(section: MenuSection): boolean {
  const prefix = `/${section.id}`;
  return route.path === prefix || route.path.startsWith(`${prefix}/`);
}

function sectionDefaultTo(section: MenuSection): string {
  const first = visibleItems(section)[0];
  if (first && typeof first.to === 'string') return first.to;
  return `/${section.id}`;
}

function visibleItems(section: MenuSection) {
  return section.items.filter((item) => {
    if (item.hidden) return false;
    if (item.requiresPermission) {
      return jwtHasPermission(section.id as ApiAudience, item.requiresPermission);
    }
    return true;
  });
}

function updateFade() {
  const el = scrollEl.value;
  if (!el) return;

  const { scrollTop, scrollHeight, clientHeight } = el;
  const canScroll = scrollHeight > clientHeight + 1;

  fadeTop.value = canScroll && scrollTop > 4;
  fadeBottom.value = canScroll && scrollTop + clientHeight < scrollHeight - 4;
}

onMounted(async () => {
  await nextTick();
  updateFade();

  window.addEventListener('resize', updateFade);

  if (scrollEl.value) {
    resizeObserver = new ResizeObserver(updateFade);
    resizeObserver.observe(scrollEl.value);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateFade);
  resizeObserver?.disconnect();
});

watch(
  () => [props.sections, route.path] as const,
  async () => {
    await nextTick();
    updateFade();
  },
  { deep: true },
);
</script>

<template>
  <nav class="botto-sidenav">
    <div class="botto-sidenav__logo">
      <RouterLink to="/" class="botto-sidenav__logo-link">
        <img :src="bottoLogo" alt="Botto Platform" class="botto-sidenav__logo-img" />
      </RouterLink>
    </div>

    <div class="botto-sidenav__scroll-wrap">
      <div ref="scrollEl" class="botto-sidenav__scroll" @scroll="updateFade">
        <div v-for="section in sections" :key="section.id" class="botto-sidenav__section">
          <RouterLink
            :to="sectionDefaultTo(section)"
            class="botto-sidenav__module"
            :class="{ 'is-active': isSectionActive(section) }"
          >
            <i v-if="section.icon" :class="`dx-icon dx-icon-${section.icon} botto-sidenav__icon`" />
            <span class="botto-sidenav__label">{{ section.title }}</span>
          </RouterLink>

          <ul v-if="isSectionActive(section)" class="botto-sidenav__submenu">
            <li v-for="item in visibleItems(section)" :key="item.title">
              <RouterLink
                :to="(item.to as any)"
                :class="{ active: isActive(item.to) }"
                class="botto-sidenav__item botto-sidenav__item--sub"
              >
                <span class="botto-sidenav__label">{{ item.title }}</span>
              </RouterLink>
            </li>
          </ul>
        </div>
      </div>

      <div class="botto-sidenav__fade botto-sidenav__fade--top" :class="{ 'is-visible': fadeTop }" />
      <div class="botto-sidenav__fade botto-sidenav__fade--bottom" :class="{ 'is-visible': fadeBottom }" />
    </div>
  </nav>
</template>

<style scoped>
ul { list-style: none; margin: 0; padding: 0; }
</style>
